import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { ArrowLeft, ArrowUp, ArrowDown, Minus, Download } from "lucide-react";
import { comparisonMoney, comparisonYears } from "@/lib/comparison";
import { num, money, moneyM, pct, unit, perUnit, perYr } from "@/lib/numfmt";

const usd = (n) => `${money(n || 0, 0)}`;
const fmt = (n, d = 1) => num(n || 0, d);

const METRICS = [
  { k: "sector", label: "Sector", type: "input", none: "—" },
  { k: "country", label: "Country", type: "input" },
  { k: "grid_status", label: "Grid", type: "input", fmt: (v) => v?.replace(/_/g, " ") },
  { k: "priority", label: "Priority", type: "input", fmt: (v) => v?.replace(/_/g, " ") },
  { k: "peak_kw", label: "Peak load (kW)", type: "input.echo", fmt: (v) => fmt(v) },
  { k: "annual_kwh", label: "Annual demand (kWh)", type: "input.echo", fmt: (v) => fmt(v, 0) },
  { k: "solar_kw", label: "Solar PV (kW)", type: "model", fmt: (v) => fmt(v) },
  { k: "battery_kwh", label: "Battery (kWh)", type: "model", fmt: (v) => fmt(v) },
  { k: "genset_kw", label: "Diesel (kW)", type: "model", fmt: (v) => fmt(v) },
  { k: "wind_kw", label: "Wind (kW)", type: "model", fmt: (v) => fmt(v) },
  { k: "renewable_fraction", label: "Renewable fraction", type: "model", fmt: (v) => `${Math.round((v || 0) * 100)}%`, higher_better: true },
  { k: "annual_demand_kwh", label: "Annual demand (kWh)", type: "model", fmt: (v) => fmt(v, 0) },
  { k: "annual_served_kwh", label: "Annual served (kWh)", type: "model", fmt: (v) => fmt(v, 0) },
  { k: "total", label: "CAPEX (total)", type: "capex", fmt: usd, lower_better: true },
  { k: "annual", label: "OPEX / year", type: "opex", fmt: usd, lower_better: true },
  { k: "lcoe_usd_per_kwh", label: "LCOE ($/kWh)", type: "fin", fmt: (v) => `$${v}`, lower_better: true },
  { k: "simple_payback_years", label: "Payback vs comparison case (yr)", type: "fin", fmt: (v) => comparisonYears(v).replace(" yr", ""), lower_better: true },
  { k: "annual_savings_vs_diesel_yr1", label: "Annual savings Y1", type: "fin", fmt: (v) => comparisonMoney(v, usd), higher_better: true },
];

export const Compare = () => {
  const t = useT();
  const [sp] = useSearchParams();
  const preselect = sp.get("ids")?.split(",").filter(Boolean) || [];
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null); // null = loading, [] = loaded empty
  const [selected, setSelected] = useState(preselect);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => { toast.error("Could not load projects"); setProjects([]); });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (selected.length < 2) { setRows([]); return; }
    setBusy(true);
    api.post("/projects/compare", { project_ids: selected })
      .then((r) => setRows(r.data.projects))
      .catch(() => toast.error("Could not compare"))
      .finally(() => setBusy(false));
  }, [selected]);

  const toggle = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 4) { toast.info("Compare up to 4 projects at a time"); return s; }
      return [...s, id];
    });
  };

  const cell = (p, m) => {
    const r = p.result || {};
    const buckets = { model: r.model, capex: r.capex, opex: r.opex, fin: r.financials, input: p.input, "input.echo": r.inputs_echo };
    const b = buckets[m.type] || {};
    return b[m.k];
  };

  const trendIcon = (m, vals) => {
    if ((!m.higher_better && !m.lower_better) || vals.length < 2) return null;
    const nums = vals.map(Number).filter((v) => !isNaN(v));
    if (nums.length < 2) return null;
    const best = m.lower_better ? Math.min(...nums) : Math.max(...nums);
    const worst = m.lower_better ? Math.max(...nums) : Math.min(...nums);
    return { best, worst };
  };

  const cmp = rows.length >= 2;

  const exportCsv = () => {
    if (!cmp) return;
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Metric", ...rows.map((p) => p.name)];
    const lines = [headers.map(escape).join(",")];
    METRICS.forEach((m) => {
      const cells = rows.map((p) => {
        const raw = cell(p, m);
        return m.fmt && raw !== undefined && raw !== null ? m.fmt(raw) : (raw ?? "");
      });
      lines.push([m.label, ...cells].map(escape).join(","));
    });
    const csv = "\uFEFF" + lines.join("\n"); // BOM for Excel utf-8
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Power_Configurator_comparison_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="App" style={{ background: "var(--paper)" }}>
      <Header />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
          <div>
            <button onClick={() => navigate("/projects")} className="btn-ghost !p-0 mb-2" data-testid="cmp-back">
              <ArrowLeft size={13} className="inline" /> {t("cmp.back")}
            </button>
            <div className="overline mb-1">{t("cmp.overline")}</div>
            <h1 className="font-display text-3xl">{t("cmp.title")}</h1>
            <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
              {t("cmp.desc")}
            </p>
          </div>
          {cmp && (
            <button onClick={exportCsv} className="btn-secondary" data-testid="cmp-export-csv">
              <Download size={13} /> {t("cmp.export_csv")}
            </button>
          )}
        </div>

        <div className="card p-6 mb-6">
          <div className="overline mb-3">{t("cmp.saved")}</div>
          {projects === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cmp-skeleton">
              {[0, 1, 2, 3, 4, 5].map((k) => (
                <div key={k} className="p-4 rounded-md border animate-pulse" style={{ borderColor: "var(--hairline)", background: "var(--paper)" }}>
                  <div className="h-2 w-24 rounded" style={{ background: "var(--hairline)" }} />
                  <div className="h-3 w-40 mt-3 rounded" style={{ background: "var(--hairline)" }} />
                  <div className="h-3 w-32 mt-2 rounded" style={{ background: "var(--hairline)" }} />
                </div>
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-sm" style={{ color: "var(--muted)" }}>
              No projects yet. <button onClick={() => navigate("/configurator")} className="link">Run a first pass →</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map((p) => {
                const on = selected.includes(p.id);
                const idx = selected.indexOf(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    className={`p-4 rounded-md border text-left transition-colors ${on ? "border-[var(--brand-green)] bg-[rgba(26,111,43,0.05)]" : "border-[var(--hairline)] hover:border-[var(--brand-green)]"}`}
                    data-testid={`cmp-select-${p.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="overline" style={{ fontSize: "0.6rem" }}>{p.input?.sector} · {p.input?.country}</div>
                        <div className="font-display text-sm mt-1" style={{ color: "var(--charcoal)" }}>{p.name}</div>
                      </div>
                      {on && <span className="pill" style={{ background: "var(--brand-green)", color: "white" }}>#{idx + 1}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {busy && <div style={{ color: "var(--muted)" }}>Loading comparison…</div>}

        {cmp && (
          <div className="card overflow-auto" data-testid="cmp-table">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--hairline)" }}>
                  <th className="text-left p-4 overline" style={{ background: "var(--paper)" }}>Metric</th>
                  {rows.map((p, i) => (
                    <th key={p.id} className="text-left p-4" style={{ minWidth: 180, background: "var(--paper)" }}>
                      <div className="overline" style={{ fontSize: "0.6rem" }}>Scenario {i + 1}</div>
                      <div className="font-display text-base mt-1" style={{ color: "var(--charcoal)" }}>{p.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRICS.map((m) => {
                  const vals = rows.map((p) => cell(p, m));
                  const t = trendIcon(m, vals);
                  return (
                    <tr key={m.label} style={{ borderBottom: "1px solid var(--hairline)" }}>
                      <td className="p-4" style={{ color: "var(--body)" }}>{m.label}</td>
                      {vals.map((v, i) => {
                        const num = Number(v);
                        let colorStyle = { color: "var(--charcoal)" };
                        let icon = null;
                        if (t && !isNaN(num)) {
                          if (num === t.best) { colorStyle = { color: "var(--brand-green)", fontWeight: 700 }; icon = <ArrowUp size={12} className="inline" />; }
                          else if (num === t.worst) { colorStyle = { color: "var(--orange-spark)" }; icon = <ArrowDown size={12} className="inline" />; }
                          else icon = <Minus size={12} className="inline opacity-40" />;
                          if (m.lower_better) icon = null; // avoid confusing arrows for lower-better metrics
                        }
                        const cellKey = `${m.label}-${rows[i]?.id || i}`;
                        return (
                          <td key={cellKey} className="p-4 font-mono" style={colorStyle}>
                            {m.fmt ? m.fmt(v) : (v ?? "—")}
                            {icon && <span className="ml-1">{icon}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!cmp && !busy && (
          <div className="card p-10 text-center">
            <div className="font-display text-xl">{t("cmp.pick_at_least_2")}</div>
            <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>{t("cmp.pick_at_least_2_sub")}</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};
